import { BrandLink } from '@/components/brand-link';
import { Accordions } from '@/components/pages/product-details/product-details-accordions';
import { ProductQuantityComponent } from '@/components/pages/product-details/product-quantity-component';
import { ProductWastageSwitch } from '@/components/pages/product-details/product-wastage-switch';
import { buttonVariants } from '@/components/ui/button';
import { siteConfig } from '@/site-config';
import {
  ProductButtons,
  ProductDetailsBackInStockForm,
  ProductDetailsBreadcrumbs,
  ProductDetailsVariants,
  ProductQuantity,
  ProductDetailsForm as SharedProductDetailsForm,
} from '@veranda/shared/components/pages/product-details';
import type { ProductPageProps } from '@veranda/shared/components/pages/product-details/client';
import { cn } from '@veranda/shared/lib';
import { Suspense } from 'react';

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export function Form({
  product,
}: {
  product: ProductPageProps;
}) {
  if (!product) return null;

  const variantPrice = product.variants?.at(0)?.price;
  const variantCompareAtPrice = product.variants?.at(0)?.compareAtPrice;
  const priceIsZero = variantPrice === 0;
  const isPreOrder = !!product.preOrderTag;
  const isGiftCard = product.type === 'GIFT_CARD';

  return (
    <SharedProductDetailsForm className="[--color:var(--color-brand-burgundy)]">
      <SharedProductDetailsForm.Breadcrumbs>
        <ProductDetailsBreadcrumbs productTitle={product.title} />
      </SharedProductDetailsForm.Breadcrumbs>
      <SharedProductDetailsForm.TitleSection>
        {product.title && (
          <SharedProductDetailsForm.Title className="typography-clash-h3">
            {product.title}
            {isPreOrder && product.preOrderTag && (
              <span className="block text-red-600">
                – PRE ORDER FOR {product.preOrderTag.split(':')[1]}
              </span>
            )}
          </SharedProductDetailsForm.Title>
        )}
        {product.brand?.name && product.brand?.link?.slug && (
          <BrandLink name={product.brand.name} slug={product.brand.link.slug} />
        )}
      </SharedProductDetailsForm.TitleSection>
      {product.productGid && (
        <SharedProductDetailsForm.Rating
          productGid={product.productGid}
          className="![&>*]:text-xs text-primary"
        />
      )}
      {variantPrice && !priceIsZero ? (
        <SharedProductDetailsForm.Price
          price={variantPrice}
          compareAtPrice={variantCompareAtPrice ?? null}
          squareMetersPerBox={product.squareMetersPerBox}
          textSize="lg"
        />
      ) : null}
      <SharedProductDetailsForm.Variants>
        {product.productFamily && (
          <ProductDetailsVariants
            productFamily={product.productFamily}
            currentSlug={product.slug}
          />
        )}
      </SharedProductDetailsForm.Variants>
      {product.type === 'GIFT_CARD' && product.giftCardConfiguration && (
        <SharedProductDetailsForm.GiftCardSelector
          product={product}
          domain={siteConfig.baseUrl}
          variants={product.giftCardConfiguration}
          buttonClassNames={cn(buttonVariants(), 'w-full')}
        />
      )}
      {product.category && !priceIsZero && !isGiftCard && (
        <SharedProductDetailsForm.Quantity>
          <Suspense
            fallback={
              <ProductQuantityComponent
                productType={product.category}
                squareMetersPerBox={product.squareMetersPerBox}
                isStatic={true}
              />
            }
          >
            <ProductQuantity
              domain={siteConfig.baseUrl}
              productType={product.category}
              squareMetersPerBox={product.squareMetersPerBox}
              productGid={product.productGid ?? null}
              variantGid={product.variants?.[0]?.gid ?? null}
              allowBackorder={product.allowBackorder ?? false}
              wastageSwitch={<ProductWastageSwitch product={product} />}
              quantityInputClassName="flex w-full select-none items-center border border-primary/20 px-4 py-[14px] text-primary max-md:text-sm"
              hintClassName="mt-0.5 text-primary text-xs"
            />
          </Suspense>
        </SharedProductDetailsForm.Quantity>
      )}
      {priceIsZero && !product.fileDownloadURL ? null : (
        <SharedProductDetailsForm.Buttons>
          {product.productGid && product.variants?.at(0)?.gid && (
            <ProductButtons.ProductButtons
              product={product}
              productSlug={product.slug}
              backInStockForm={
                <ProductDetailsBackInStockForm
                  variantGid={product.variants?.[0]?.gid ?? ''}
                  titleClassName="typography-clash-h3 text-primary font-medium tracking-wide"
                  descriptionClassName="typography-satoshi-body text-primary"
                  triggerButtonClassNames={cn(buttonVariants(), 'w-full')}
                  submitButtonClassNames={cn(buttonVariants(), 'h-auto w-full')}
                />
              }
              domain={siteConfig.baseUrl}
              addToCartButtonClassName={cn(buttonVariants(), 'w-full')}
              sampleButtonClassName={cn(
                buttonVariants({ variant: 'primary-outline' }),
                'w-full'
              )}
            />
          )}
        </SharedProductDetailsForm.Buttons>
      )}
      {product.defaultSettings?.promotionalImage && (
        <SharedProductDetailsForm.PromotionalImage
          image={product.defaultSettings.promotionalImage}
        />
      )}
      {isPreOrder && <SharedProductDetailsForm.PreOrderInfo />}
      {product.complimentaryProducts && (
        <SharedProductDetailsForm.ComplimentaryProducts
          products={product.complimentaryProducts}
          buttonClassNames={cn(buttonVariants({ size: 'sm' }))}
          titleClassName="typography-outfit-body"
          domain={siteConfig.baseUrl}
        />
      )}
      <SharedProductDetailsForm.Accordion>
        <Accordions product={product} />
      </SharedProductDetailsForm.Accordion>
    </SharedProductDetailsForm>
  );
}
